import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Spinner from 'react-bootstrap/Spinner'
import Swal from 'sweetalert2';
import FormCheck from 'components/atoms/form-check';
import FormGroup from 'components/organisms/forms/form-group';
import FormIndividual from 'components/organisms/forms/form-individual';
import FormPartnership from 'components/organisms/forms/form-partnership';
import ArrowRight from 'components/atoms/icons/arrow-right';
import ArrowLeft from 'components/atoms/icons/arrow-left';
import {
    getRegistrationByBillNo,
    postPartnershipPaymentConfirmation,
    postRegistrationGroup,
    postRegistrationIndividual,
    postRegistrationPartnership
} from 'datas/registration';
import {
    getTransactionByPersonId,
    postGroupRegistrationTransactionNotification,
    postPartnershipDanaTransactionNotification,
    postPartnershipDriverTransactionNotification,
    postRegistrationTransactionNotification,
    postTransaction,
    TransactionItem
} from 'datas/transaction';
import { useRegistration } from 'stores/registration';
import { useAuth } from 'stores/auth';
import * as Constant from 'constants/registration';
import { ticketPrice } from 'constants/transaction';
import PreviewIndividual from 'components/organisms/previews/preview-individual';
import PreviewGroup from 'components/organisms/previews/preview-group';
import PreviewPartnership from 'components/organisms/previews/preview-partnership';
import FormPartnershipPayment from 'components/organisms/forms/form-partnership-payment';

export const Registration = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const profile = useAuth().profile;
    const { payloadIndividual, payloadGroup, payloadPartnership, payloadChildren, clearAllPayload, parnetshipPaymentImage } = useRegistration();
    
    const [step, setStep] = useState<Constant.STEP>(Constant.STEP.SEGMENTATION);
    const [validated, setValidated] = useState(false);
    const [billNo, setBillNo] = useState('');
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [formType]  = useState<Constant.FORM_TYPE>(Constant.FORM_TYPE.BIBLE_STUDY);
    const [registrationType, setRegistrationType] = useState<Constant.REGISTRATION_TYPE>();
    const [isLoading, setIsLoading] = useState(false);
    
    const postPartnershipRegistration = async () => {
        const { registration_number } = await postRegistrationPartnership(
            payloadPartnership as Constant.PayloadPartnership, 
            profile!.person_id
        );
        const isDriverPartnership = payloadPartnership?.partnership_category === Constant.PARTNERSHIP_CATEGORY.DRIVER;
    
        if (isDriverPartnership) {
            postPartnershipDriverTransactionNotification({ registrationNumber: registration_number });
        }
    
        setRegistrationNumber(registration_number);
    };
    
    const postIndividualRegistration = async () => {
        const { guardian_count, ...individual } = payloadIndividual as Constant.PayloadIndividual;
        let guardianCount = typeof guardian_count === 'string' 
            ? parseInt(guardian_count) : guardian_count;
        if (!(guardianCount > 0)) guardianCount = 0;

        const pickedUpTicket = Constant.child_pick_up.find(
            ({ value }) => value === Constant.PICKUP_TYPE.PICKEDUP
        );

        // Generate ticket for children
        const transactionItems = payloadChildren.map(({ name }) => {
            const ticketId = Constant.child_pick_up.find(
                ({ value }) => value === payloadIndividual?.pick_up_type
            );
            return {
                item_id: ticketId?.ticketId ?? pickedUpTicket?.ticketId as string,
                qty: 1,
                notes: name,
            }
        });

        if (guardianCount > 0) {
            // Generate ticket for guardian (pendamping)
            transactionItems.push({
                item_id: pickedUpTicket?.ticketId as string,
                qty: guardianCount,
                notes: 'Pendamping'
            });
        }
        
        let { bill_no, redirect_url } = await getTransactionDetails(transactionItems);

        setBillNo(bill_no);
    
        const payload = {
            ...individual as Constant.PayloadIndividual,
            children: payloadChildren.map(child => ({
                ...child,
                guardian_count: guardianCount,
                pick_up_type: payloadIndividual?.pick_up_type ?? ''
            }))
        };
        await postRegistrationIndividual(payload, profile!.person_id, bill_no, redirect_url);
    
        const nominal = (guardianCount + payloadChildren.length) * ticketPrice;
        postRegistrationTransactionNotification({
            registrationNumber: bill_no,
            redirectUrl: redirect_url,
            nominal
        });

        await getTransactionByPersonId(profile?.person_id ?? '');
    };

    const postGroupRegistration = async () => {
        const payload = {
            ...payloadGroup as Constant.PayloadGroup,
            student_count: payloadChildren.length,
            students: payloadChildren.map(({ name, grade, gender }) => ({ name, grade, gender }))
        } as Constant.PayloadGroup;
        const { registration_number } = await postRegistrationGroup(payload as Constant.PayloadGroup, profile!.person_id);
        postGroupRegistrationTransactionNotification({
            ...payload,
            registrationNumber: registration_number,
        });
        setRegistrationNumber(registration_number);
    };
    
    const getTransactionDetails = async (transaction: TransactionItem[]) => {
        let bill_no = billNo, redirect_url = '';
    
        if (!bill_no) {
            const response = await postTransaction(profile!.person_id, transaction);
            bill_no = response.bill_no;
            redirect_url = response.redirect_url;
        }
    
        return { bill_no, redirect_url };
    };

    const onPrevStep = () => {
        setStep(prev => (parseInt(prev) - 1).toString() as Constant.STEP);
    }

    const onNextStep = () => {
        setStep(prev => (parseInt(prev) + 1).toString() as Constant.STEP);
    }

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
    
        const form = e.currentTarget;
        if (form.checkValidity() === false) {
            setValidated(true);
            Swal.fire(
                'Data Belum Benar',
                '* Menunjukkan pertanyaan wajib diisi. Pastikan semua data terisi dengan benar.',
                'error'
            );
            return;
        }
    
        try {
            setIsLoading(true);
    
            if (step === Constant.STEP.FORM || step === Constant.STEP.PREVIEW) {
                if (formType === Constant.FORM_TYPE.PARTNERSHIP) {
                    await postPartnershipRegistration();
                } else if (registrationType === Constant.REGISTRATION_TYPE.INDIVIDUAL) {
                    await postIndividualRegistration();
                } else if (registrationType === Constant.REGISTRATION_TYPE.GROUP) {
                    await postGroupRegistration();
                }
            }
    
            onNextStep();
        } catch (err: any) {
            const message = typeof err === 'string' 
                ? err
                : err?.response?.data?.message ?? 'Terjadi kesalahan pada proses pendaftaran';
    
            Swal.fire(
                'Pendaftaran Belum Berhasil',
                message,
                'error'
            );
        } finally {
            setValidated(false);
            setIsLoading(false);
        }
    }

    const onUploadPaymentConfirmation = async () => {
        const file = parnetshipPaymentImage;
        if (file) {
            try {
                setIsLoading(true);
                const data = new FormData();
                data.append('payment-confirmation', file);

                const dataRegistration = await getRegistrationByBillNo({
                    regNo: registrationNumber,
                    singleSnapshot: true
                });
                if (dataRegistration) {
                    await postPartnershipPaymentConfirmation(dataRegistration.id, data)
                        .then(async () => {
                            await postPartnershipDanaTransactionNotification({
                                registrationNumber: registrationNumber,
                                eventLocation: dataRegistration.event_location,
                                nominal: dataRegistration.sponsored_children_count * ticketPrice
                            });
                            
                            Swal.fire({
                                icon: 'success',
                                title: 'Berhasil Unggah Bukti Pembayaran',
                                text: 'Pembayaran Anda terverifikasi maksimal 1x24 jam hari kerja setelah pembayaran dilakukan, dan akan menerima notifikasi melalui aplikasi GMS Church.'
                            }).then(() => {
                                onNextStep();
                            });
                        })
                        .catch(() => {
                            Swal.fire({
                                icon: 'error',
                                title: 'Belum Berhasil Unggah Bukti Pembayaran',
                                text: 'Pastikan bahwa foto dokumen yang Anda unggah memiliki format dan tipe file JPG, JPEG atau PNG.'
                            });
                        })
                }
            } catch (e) {

            } finally {
                setIsLoading(false);
            }
        }
    }

    useEffect(() => {
        const bill_no = location.state?.billNo;
        if (bill_no) setBillNo(bill_no);
        return () => {
            setBillNo('');
            clearAllPayload();
        }
    }, [])

    useEffect(() => {
        if (window) {
            window.scrollTo(0, 0);
        }
    }, [step])

    const stepProgressPct = useMemo(() => {
        let totalPage = 3;
        if (formType === Constant.FORM_TYPE.PARTNERSHIP && payloadPartnership?.partnership_category === Constant.PARTNERSHIP_CATEGORY.DRIVER) {
            totalPage = 2;
        }
        return parseInt(step) / (totalPage) * 100
    }, [step, formType])
    
    return (
        <section className='registration'>
            <ProgressBar now={stepProgressPct} className='registration_progress' />
            
            <Form noValidate validated={validated} onSubmit={onSubmit}>

                {/* segmentation */}
                {step === Constant.STEP.SEGMENTATION && (
                    <Form.Group>
                        <div className='mb-4'>
                            <h2>Pendaftaran Bible Study Eaglekidz</h2>
                            <span className='text-danger'>* Menunjukkan pertanyaan wajib diisi</span>
                        </div>
                        {/* <Card className='mb-2'>
                            <Card.Body>
                                <FormCheck
                                    required
                                    label='Jenis Pendaftaran'
                                    value={formType ?? ''}
                                    options={Constant.form_type}
                                    onChange={(value) => setFormType(value as Constant.FORM_TYPE)}
                                />
                                <span className="separator" />
                                <Form.Control.Feedback type="invalid">
                                    Pertanyaan wajib diisi
                                </Form.Control.Feedback>
                            </Card.Body>
                        </Card> */}
                        
                        {formType === Constant.FORM_TYPE.BIBLE_STUDY && (
                            <Card>
                                <Card.Body>
                                    <FormCheck
                                        required={formType === Constant.FORM_TYPE.BIBLE_STUDY}
                                        label='Kategori Pendaftaran'
                                        value={registrationType ?? ''}
                                        options={Constant.registration_type}
                                        onChange={(value) => setRegistrationType(value as Constant.REGISTRATION_TYPE)}
                                    />
                                    <span className="separator" />
                                    <Form.Control.Feedback type="invalid">
                                        Pertanyaan wajib diisi
                                    </Form.Control.Feedback>
                                </Card.Body>
                            </Card>
                        )}
                    </Form.Group>
                )}

                {/* form */}
                {step === Constant.STEP.FORM
                    ? formType === Constant.FORM_TYPE.PARTNERSHIP
                        ? <FormPartnership />
                        : registrationType === Constant.REGISTRATION_TYPE.INDIVIDUAL
                            ? <FormIndividual />
                            : <FormGroup />
                    : null
                }

                {/* preview */}
                {step === Constant.STEP.PREVIEW
                    ? formType === Constant.FORM_TYPE.PARTNERSHIP
                        ? payloadPartnership?.partnership_category === Constant.PARTNERSHIP_CATEGORY.DANA
                            ? <FormPartnershipPayment registrationNumber={registrationNumber} /> 
                            : <PreviewPartnership registrationNumber={registrationNumber} />
                        : registrationType === Constant.REGISTRATION_TYPE.INDIVIDUAL
                            ? <PreviewIndividual billNo={billNo} />
                            : <PreviewGroup registrationNumber={registrationNumber} />
                    : null
                }

                {/* partnership data payment confirmation */}
                {step === Constant.STEP.PAYMENT_SUCCESS &&
                    <PreviewPartnership registrationNumber={registrationNumber} />
                }
                
                <div className='registration_footer'>
                    {step === Constant.STEP.FORM &&
                        <Button
                            className='d-flex align-items-center justify-content-center gap-3'
                            onClick={onPrevStep}
                            disabled={isLoading}
                        >
                            <ArrowLeft />
                            Kembali
                        </Button>
                    }

                    { step === Constant.STEP.SEGMENTATION || step === Constant.STEP.FORM ?
                        <Button
                            type='submit'
                            className='d-flex align-items-center justify-content-center gap-3'
                            disabled={isLoading}
                        >
                            {step === Constant.STEP.SEGMENTATION  ? 'Selanjutnya' :
                                step === Constant.STEP.FORM ? 'Kirim'
                                : 'Selesai'
                            }
                            {isLoading ?
                                <Spinner size='sm' variant='light' />
                            :
                                <ArrowRight />
                            }
                        </Button>
                    : payloadPartnership?.partnership_category === Constant.PARTNERSHIP_CATEGORY.DANA ?
                        step === Constant.STEP.PREVIEW ?
                            <Button
                                type='button'
                                className='d-flex align-items-center justify-content-center gap-3'
                                onClick={onUploadPaymentConfirmation}
                                disabled={isLoading || !parnetshipPaymentImage}
                            >
                                Kirim
                                {isLoading ?
                                    <Spinner size='sm' variant='light' />
                                :
                                    <ArrowRight />
                                }
                            </Button>
                            : step === Constant.STEP.PAYMENT_SUCCESS ? 
                            <Button
                                type='button'
                                className='d-flex align-items-center justify-content-center gap-3'
                                onClick={() => {
                                    navigate('/transaction');
                                }}
                                disabled={isLoading}
                            >
                                Selesai
                            </Button>
                            : null
                    : null}
                </div>
            </Form>
        </section>
    )
}